<script setup lang="ts">
/**
* ScholarTrip 2.0 - 高密度專業搜尋中樞 (Production Ready)
* 🚨 [架構修復]：
* 1. 版面穩定性 (CLS 優化)：為 Search Panel 容器預留最小高度，防止切換 Tab 時畫面劇烈跳
動。
* 2. 結構化資料 (AEO/SEO)：精確注入 Google SearchAction，讓首頁成為 Answer Engine 的入
口。
* 3. 行動端優化：加入 touch-pan-x 與 snap-mandatory 提升橫向滑動手感。
*/

const props = defineProps<{ active?: string }>();
// 10 種專業旅遊產品清單 (與 BFF 接口對齊)
const categories = [
  { id: "flights", label: "Flights", icon: "ph:airplane-tilt-bold" },
  { id: "hotels", label: "Hotels", icon: "ph:buildings-bold" },
  { id: "packages", label: "Packages", icon: "ph:suitcase-rolling-bold" },
  { id: "cruises", label: "Cruises", icon: "ph:anchor-bold" },
  { id: "cars", label: "Cars", icon: "ph:car-bold" },
  { id: "trains", label: "Trains", icon: "ph:train-bold" },
  { id: "transfers", label: "Transfers", icon: "ph:taxi-bold" },
  { id: "insurance", label: "Insurance", icon: "ph:shield-check-bold" },
  { id: "activities", label: "Activities", icon: "ph:map-trifold-bold" },
  { id: "visas", label: "Visas", icon: "gravity-ui:passport" },
];
const activeCategory = ref(props.active || "flights");
// AEO/GEO: 注入 SearchAction 結構化數據，讓 Google 爬蟲直接理解搜尋入口
useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: "https://www.scholartrip.com/",
        potentialAction: {
          "@type": "SearchAction",
          target:
            "https://www.scholartrip.com/flights/search?origin={origin}&destination={destination}",
          "query-input": "required name=origin",
        },
      }),
    },
  ],
});
</script>
<template>
  <div class="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- 核心搜尋容器 (高密度設計) -->
    <div
      class="bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] overflow-hidden animate-fade-in-up border border-slate-100/50"
      style="animation-delay: 0.1s"
    >
      <!-- 產品類別切換 (行動端橫向 Snap 滾動，無捲軸，原生 App 體驗) -->
      <div
        class="flex items-center gap-2 overflow-x-auto hide-scrollbar bg-slate-50/80 p-2 border-b border-slate-100 snap-x snap-mandatory touch-pan-x"
      >
        <button
          v-for="cat in categories"
          :key="cat.id"
          @click="activeCategory = cat.id"
          class="flex items-center gap-2 px-5 py-3 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all whitespace-nowrap snap-start outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          :class="
            activeCategory === cat.id
              ? 'bg-white text-brand-600 shadow-sm border border-slate-200/50'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-200/50 transparent border border-transparent'
          "
          :aria-selected="activeCategory === cat.id"
          role="tab"
        >
          <Icon :name="cat.icon" class="text-lg" />
          {{ cat.label }}
        </button>
      </div>
      <!-- 搜尋表單區塊 (固定最小高度防止版面跳動) -->
      <div class="p-5 md:p-8 lg:p-10 min-h-[320px] relative">
        <div class="flex items-center gap-2 mb-6">
          <span class="relative flex h-2.5 w-2.5">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"
            ></span>
            <span
              class="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"
            ></span>
          </span>
          <span
            class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
          >
            Live Official Inventory Verified
          </span>
        </div>
        <Transition name="fade-slide" mode="out-in">
          <!-- 根據類別切換不同的 Search Panel -->
          <div :key="activeCategory" class="w-full">
            <FlightsSearchPanel
              v-if="
                activeCategory === 'flights' || activeCategory === 'packages'
              "
              button-label="Search Now"
            />
            <!-- 其他產品的 Integration Placeholder -->
            <div
              v-else
              class="flex flex-col items-center justify-center py-16 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200"
            >
              <Icon
                :name="
                  categories.find((c) => c.id === activeCategory)?.icon ||
                  'ph:circle'
                "
                class="text-5xl text-slate-300 mb-4"
              />
              <h4 class="text-xl font-black text-slate-900 uppercase italic">
                {{ activeCategory }} Module
              </h4>
              <p
                class="text-slate-500 font-bold text-xs mt-2 uppercase tracking-widest"
              >
                Connecting to Partner API...
              </p>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
<style scoped>
/* 針對切換面板加入輕微的位移，增加流暢度 */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
